import { useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from 'react'
import { type AuthStatus, type LogoutReason, AuthContext } from './useAuth'
import type { PortalRole, SessionResponse } from '../lib/types'
import { AUTH_PATHS, REAUTH_LOGIN_PATH } from '../lib/routes'
import {
  AUTH_EVENTS,
  PORTAL_ACCESS_DENIED_CODE,
  REAUTH_REQUIRED_CODE,
  SESSION_EXPIRED_CODE,
  apiFetch,
  renewSession,
} from '../lib/api'

const _SESSION_KEY = 'billing_portal_session_flag'
const _PROFILE_KEY = 'billing_portal_profile'
const _REASON_KEY = 'billing_portal_auth_reason'
// MS-977: when a sign-out was set off just now and leaves through the
// provider's logout (Cognito), so the badge survives the full page load back
// to /login. One-shot (removed on the next mount) and short-lived, so it is
// never mistaken for a sign-out from some earlier visit.
const _PROVIDER_LOGOUT_KEY = 'billing_portal_provider_logout_at'
const PROVIDER_LOGOUT_MAX_AGE_MS = 2 * 60_000

function returningFromProviderLogout(): boolean {
  if (localStorage.getItem(_SESSION_KEY) === '1') return false
  const at = Number(localStorage.getItem(_PROVIDER_LOGOUT_KEY))
  if (!Number.isFinite(at) || at <= 0) return false
  const age = Date.now() - at
  return age >= 0 && age < PROVIDER_LOGOUT_MAX_AGE_MS
}

interface Profile {
  displayName: string | null
  email: string | null
  accountId: string | null
  accountName: string | null
  role: PortalRole | null
}

const EMPTY_PROFILE: Profile = {
  displayName: null,
  email: null,
  accountId: null,
  accountName: null,
  role: null,
}

/** The pages the mount probe leaves alone: the callback and the account
 * choice drive login/logout themselves (there is no session to find there
 * yet, only the pending-login cookie), and Access denied is where a revoked
 * session has just been sent — probing there would only find the same 403. */
const UNPROBED_PATHS: readonly string[] = [
  AUTH_PATHS.oauthCallback,
  AUTH_PATHS.selectAccount,
  AUTH_PATHS.accessDenied,
]

/** Leaves the app for a fresh page load at `path` (MS-997 D10, G5): the
 * revoked or ended session's cached queries go with it, and the Gate renders
 * the destination from a clean start. */
function leaveTo(path: string) {
  globalThis.location.replace(path)
}

function codeOf(body: unknown): string | undefined {
  return body && typeof body === 'object' && 'code' in body && typeof body.code === 'string' ? body.code : undefined
}

function loadProfile(): Profile {
  try {
    const raw = localStorage.getItem(_PROFILE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<Profile>
      return { ...EMPTY_PROFILE, ...parsed }
    }
  } catch {
    /* ignore */
  }
  return EMPTY_PROFILE
}

export function AuthProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [status, setStatus] = useState<AuthStatus>(() =>
    localStorage.getItem(_SESSION_KEY) === '1' ? 'authenticated' : 'checking'
  )
  // A Cognito sign-out (MS-977) leaves through the provider's logout and
  // comes back to /login as a full page load, so the in-memory flag is lost;
  // the marker logout('provider_logout') stored restores the badge for that
  // one return trip only. Any other stored reason is dropped silently below.
  const [didLogOut, setDidLogOut] = useState(returningFromProviderLogout)
  const [didExpire, setDidExpire] = useState(false)
  const [profile, setProfile] = useState(loadProfile)
  // True until the mount probe has answered (MS-997 D1): the cached profile
  // may carry a role the grant no longer has, so a role guard waits for it.
  const [probing, setProbing] = useState(() => !UNPROBED_PATHS.includes(window.location.pathname))
  const statusRef = useRef(status)
  statusRef.current = status
  // Bumped by every login/logout. The mount probe only applies its result if
  // no explicit login/logout happened while its request was in flight —
  // otherwise a probe that left with the old cookie, and answers 200 just
  // after the user signs out, would sign them straight back in (and the
  // shell's next request would then 401 into "session expired").
  const authEpoch = useRef(0)

  // Consume the provider-logout marker: it is honoured by the initial state
  // above on this mount and never again.
  useEffect(() => {
    localStorage.removeItem(_PROVIDER_LOGOUT_KEY)
  }, [])

  // login/logout, not one setAuth(authenticated, ...) — a boolean flag
  // choosing between two barely-related bodies (store a profile vs. record
  // a reason) was really two APIs sharing a signature (Sonar
  // typescript:S2301).
  const login = useCallback((newProfile?: Partial<Profile>) => {
    authEpoch.current += 1
    localStorage.setItem(_SESSION_KEY, '1')
    localStorage.removeItem(_REASON_KEY)
    localStorage.removeItem(_PROVIDER_LOGOUT_KEY)
    setDidLogOut(false)
    setDidExpire(false)
    if (newProfile) {
      const merged: Profile = { ...EMPTY_PROFILE, ...newProfile }
      localStorage.setItem(_PROFILE_KEY, JSON.stringify(merged))
      setProfile(merged)
    }
    setStatus('authenticated')
  }, [])

  // 'provider_logout' is a sign-out about to leave the SPA through the
  // provider's own logout page (Cognito); otherwise the same as 'logout'.
  // 'denied' (a revoked grant, D10) ends the session with neither badge:
  // Access denied is its own screen.
  const logout = useCallback((reason?: LogoutReason) => {
    authEpoch.current += 1
    localStorage.setItem(_REASON_KEY, reason === 'expired' ? 'expired' : 'logout')
    if (reason === 'provider_logout') {
      localStorage.setItem(_PROVIDER_LOGOUT_KEY, String(Date.now()))
    } else {
      localStorage.removeItem(_PROVIDER_LOGOUT_KEY)
    }
    localStorage.removeItem(_SESSION_KEY)
    localStorage.removeItem(_PROFILE_KEY)
    setProfile(EMPTY_PROFILE)
    setDidExpire(reason === 'expired')
    setDidLogOut(reason !== 'expired' && reason !== 'denied')
    setStatus('unauthenticated')
  }, [])

  // The two ways a session ends that are not a plain expiry (MS-997): the
  // daily sign-in is due or the refresh token was refused (G5) → the sign-in page
  // with the "sign in again" notice; the grant was revoked (D10) → Access
  // denied with the grant copy, not "session expired".
  const endForReauth = useCallback(() => {
    logout('expired')
    leaveTo(REAUTH_LOGIN_PATH)
  }, [logout])
  const endForDenied = useCallback(() => {
    logout('denied')
    leaveTo(AUTH_PATHS.accessDenied)
  }, [logout])

  // Always-on mount probe — discovers a valid session cookie in new tabs.
  // Skipped on UNPROBED_PATHS (see above). An expired 60-minute session is
  // renewed through POST /auth/refresh, as lib/api.ts does for any request
  // (G5).
  useEffect(() => {
    if (UNPROBED_PATHS.includes(window.location.pathname)) return
    const hadFlag = localStorage.getItem(_SESSION_KEY) === '1'
    const epoch = authEpoch.current
    let cancelled = false
    const stale = () => cancelled || authEpoch.current !== epoch

    /** A probe that found no usable session. Signed out already (no flag):
     * just the Login screen, quietly. Otherwise the way it ended decides. */
    function endedSession(code: string | undefined, renewalFailed: boolean) {
      if (!hadFlag) {
        localStorage.removeItem(_REASON_KEY)
        setStatus('unauthenticated')
      } else if (code === PORTAL_ACCESS_DENIED_CODE) {
        endForDenied()
      } else if (renewalFailed || code === REAUTH_REQUIRED_CODE) {
        endForReauth()
      } else {
        logout('expired')
      }
    }

    async function acceptSession(r: Response) {
      const data = (await r.json()) as SessionResponse
      if (!stale()) login(data)
    }

    /** A 401 auth.session_expired: renew once, then sign in or end. */
    async function renewExpiredSession() {
      const outcome = await renewSession()
      if (stale()) return
      if (outcome.ok) login(outcome.session)
      else endedSession(outcome.code, true)
    }

    async function probe() {
      const r = await fetch('/api/v1/auth/session', { credentials: 'include' })
      if (stale()) return
      if (r.ok) {
        await acceptSession(r)
        return
      }
      if (r.status !== 401 && r.status !== 403) {
        if (!hadFlag) setStatus('unauthenticated')
        return
      }
      const code = codeOf(await r.json().catch(() => null))
      if (stale()) return
      if (r.status === 401 && code === SESSION_EXPIRED_CODE) {
        await renewExpiredSession()
        return
      }
      endedSession(code, false)
    }

    probe()
      .catch(() => {
        if (!stale() && !hadFlag) setStatus('unauthenticated')
      })
      .finally(() => setProbing(false))
    return () => {
      cancelled = true
    }
  }, [login, logout, endForDenied, endForReauth])

  // What lib/api.ts announces about any request (MS-997): a 401
  // auth.unauthorized (session expired), the daily sign-in or a refused
  // refresh token (G5), a revoked grant (D10), and a renewed session, whose profile
  // carries the current role. Only a signed-in session acts on them: the
  // sign-in pages handle their own errors.
  useEffect(() => {
    const signedIn = () => statusRef.current === 'authenticated'
    function onSessionExpired() {
      if (signedIn()) logout('expired')
    }
    function onReauthRequired() {
      if (signedIn()) endForReauth()
    }
    function onAccessDenied() {
      if (signedIn()) endForDenied()
    }
    function onSessionRefreshed(event: Event) {
      const session = (event as CustomEvent<SessionResponse | undefined>).detail
      if (signedIn() && session) login(session)
    }
    const handlers: ReadonlyArray<[string, (event: Event) => void]> = [
      [AUTH_EVENTS.sessionExpired, onSessionExpired],
      [AUTH_EVENTS.reauthRequired, onReauthRequired],
      [AUTH_EVENTS.accessDenied, onAccessDenied],
      [AUTH_EVENTS.sessionRefreshed, onSessionRefreshed],
    ]
    for (const [name, handler] of handlers) window.addEventListener(name, handler)
    return () => {
      for (const [name, handler] of handlers) window.removeEventListener(name, handler)
    }
  }, [login, logout, endForReauth, endForDenied])

  // D1: re-read the session into the profile (the role above all). Errors
  // need no handling here: apiFetch announces an expiry or a revoked grant,
  // and anything else leaves the profile as it was.
  const refreshSession = useCallback(async () => {
    try {
      const data = await apiFetch<SessionResponse>('/api/v1/auth/session')
      if (statusRef.current === 'authenticated') login(data)
    } catch {
      // See above.
    }
  }, [login])

  // Context value must be a stable reference across renders that don't
  // actually change auth state — AuthProvider wraps the whole app, so an
  // unmemoized object here would re-render every consumer on every render
  // of this component (Sonar typescript:S6481).
  const value = useMemo(
    () => ({
      isAuthenticated: status === 'authenticated',
      status,
      login,
      logout,
      refreshSession,
      userEmail: profile.email,
      userName: profile.displayName,
      accountId: profile.accountId,
      accountName: profile.accountName,
      role: profile.role,
      isPortalAdmin: profile.role === 'admin',
      roleKnown: !probing && profile.role !== null,
      didLogOut,
      didExpire,
    }),
    [status, login, logout, refreshSession, profile, probing, didLogOut, didExpire]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
